import { ButtonTeamBillingPortal } from "@/components/team/button-team-billing-portal";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { buttonVariants } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useAdminMode } from "@/contexts/admin-mode-provider";
import { useTeams } from "@/contexts/teams-provider";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { setWindowTitle } from "@/lib/window-title";
import { AlertTriangleIcon, ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

type Tab = {
  type: "link" | "separator";
  label?: string;
  value?: string;
  className?: string;
  hasAccess?: boolean;
};

export default function TeamPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { teamSelected } = useTeams();
  const { t } = useTranslation();
  const { isAdminMode } = useAdminMode();

  const defaultTab = "settings";
  const [activeTab, setActiveTab] = useState<string>(defaultTab);

  const tabList: Tab[] = [
    {
      type: "link",
      label: t("team.page.tabs.activity"),
      value: "activity",
      hasAccess: true,
    },
    {
      type: "separator",
    },
    {
      type: "link",
      label: t("team.page.tabs.knowledge"),
      value: "knowledge-base",
      hasAccess: true,
    },
    {
      type: "separator",
    },
    {
      type: "link",
      label: t("team.page.tabs.settings"),
      value: "settings",
      hasAccess: true,
    },
    {
      type: "link",
      label: t("team.page.tabs.users"),
      value: "users",
      hasAccess: true,
    },
    {
      type: "link",
      label: t("team.page.tabs.billing"),
      value: "billing",
      hasAccess: true,
    },
    {
      type: "link",
      label: t("team.page.tabs.credits"),
      value: "credits",
      hasAccess: true,
    },
    {
      type: "separator",
    },
    {
      type: "link",
      label: t("team.page.tabs.integrations"),
      value: "/integrations",
      hasAccess: true,
    },
  ];

  useEffect(() => {
    const paths = location.pathname.replace("/team", "").split("/");
    if (paths.length <= 1) {
      navigate(defaultTab, { replace: true });
      return;
    }

    // Handle nested routes - if we have a nested path like /integrations/hubspot,
    // we should treat it as the parent tab (integrations)
    const currentTab = paths[1] ?? defaultTab; // Use the first path segment after /team

    const tab = tabList.find((tab) => tab.value === currentTab);

    if (!tab?.hasAccess) {
      navigate("settings", { replace: true });
      setActiveTab("settings");
      return;
    }

    setActiveTab(currentTab);
  }, [isAdminMode, location.pathname]);

  useEffect(() => {
    setWindowTitle(
      t("team.page.windowTitle", {
        tab: tabList.find((tab) => tab.value === activeTab)?.label,
      })
    );
  }, [activeTab, t]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    navigate(value);
  };

  return (
    <>
      <div className="container flex-1 p-8 pt-6 compact:p-6 compact:pt-4 space-y-5">
        <div className="flex flex-row justify-between">
          <div className="mb-5 space-y-2">
            <h2 className="text-3xl font-bold tracking-tight">
              {t("team.page.title")}
            </h2>
            <p className="text-muted-foreground">
              {t("team.page.description")}
            </p>
          </div>
          {teamSelected?.paymentMethodMissed === true && (
            <div>
              <Alert
                variant="warning"
                className="flex flex-row space-x-3 max-w-[600px]"
              >
                <div className="flex flex-row flex-1 space-x-3">
                  <AlertTriangleIcon className="w-[60px] h-[30px]" />
                  <div className="flex flex-col">
                    <AlertTitle className="text-sm">
                      {t("team.page.paymentWarning.title")}
                    </AlertTitle>
                    <AlertDescription className="text-xs">
                      {t("team.page.paymentWarning.description")}
                    </AlertDescription>
                  </div>
                </div>
                <div className="flex flex-col justify-center gap-2">
                  <ButtonTeamBillingPortal variant="warning" size="sm" />
                </div>
              </Alert>
            </div>
          )}
        </div>
        <Separator className="my-6" />
        <div className="mt-8 grid w-full items-start gap-6 grid-cols-[200px_1fr]">
          <nav className="grid gap-4 text-sm text-muted-foreground">
            {tabList.map((tab, index) => {
              switch (tab.type) {
                case "link":
                  if (tab.value && tab.hasAccess) {
                    const isIntegrations = tab.value === "/integrations";
                    return (
                      <Link
                        to={tab.value}
                        key={tab.value}
                        onClick={(e) => {
                          if (isIntegrations) {
                            e.preventDefault();
                            window.open(tab.value, "_blank");
                          } else {
                            handleTabChange(tab.value ?? "");
                          }
                        }}
                        className={cn(
                          buttonVariants({ variant: "ghost" }),
                          (activeTab === tab.value &&
                            "font-semibold text-primary bg-muted") ||
                            "",
                          tab.className,
                          "justify-between"
                        )}
                      >
                        {tab.label}
                        {isIntegrations && <ExternalLink className="w-4 h-4" />}
                      </Link>
                    );
                  }
                  break;
                case "separator":
                  return <Separator key={index} />;
              }
              return null;
            })}
          </nav>
          <div className="grid gap-6">
            <Outlet />
          </div>
        </div>
      </div>
    </>
  );
}
